/**
 * 订阅、发布类，需要用到该功能的class可以继承此类
 * 参考 mitt 实现, https://github.com/developit/mitt
 */

export type EventType = string | symbol | '*';
export type Handler = (...args: Array<unknown>) => void;
export type EventHandlerList = Array<Handler>;
export type EventHandlerMap = Map<EventType, EventHandlerList>;

/**
 * 默认导出的 EventEmitter 类
 */
export default class EventEmitter {
  /** 所有监听的事件集合 */
  all: EventHandlerMap;
  /** on事件拦截，如果返回 false 则不监听该事件 */
  intercept: undefined | ((type: EventType, handler: Handler) => unknown);

  constructor(all?: EventHandlerMap) {
    this.all = all || new Map();
    this.intercept = undefined;
  }

  /**
   * 监听事件
   * @param type 事件名, * 为所有事件
   * @param handler 事件回调函数
   */
  on(type: EventType, handler: Handler): void {
    if (this.intercept && this.intercept(type, handler) === false) {
      return;
    }
    const handlers: EventHandlerList | undefined = this.all!.get(type);
    if (handlers) {
      handlers.push(handler);
    } else {
      this.all!.set(type, [handler]);
    }
  }

  /**
   * 监听事件，只触发一次
   * @param type 事件名, * 为所有事件
   * @param handler 事件回调函数
   */
  once(type: EventType, handler: Handler): void {
    const fn = (...args: Array<unknown>) => {
      this.off(type, fn);
      handler.apply(this, args);
    };
    this.on(type, fn);
  }

  /**
   * 移除监听事件
   * @param type 事件名, * 为所有事件
   * @param handler 事件回调函数，不传则移除事件名对应的所有事件
   */
  off(type: EventType, handler?: Handler): void {
    const handlers: EventHandlerList | undefined = this.all!.get(type);
    if (handlers) {
      if (handler) {
        handlers.splice(handlers.indexOf(handler) >>> 0, 1);
      } else {
        this.all!.set(type, []);
      }
    }
  }

  /**
   * 触发事件
   * @param type 事件名, 不能为 *
   * @param args 调用回调函数传入的参数
   */
  emit(type: EventType, ...args: Array<unknown>): void {
    if (type === '*') {
      return;
    }
    const handlers = new Array<Handler>()
      .concat(this.all!.get(type) || [])
      .concat(this.all!.get('*') || []);
    if (handlers.length) {
      handlers.map((handler) => {
        handler.apply(this, args);
      });
    }
  }
}
